<script setup lang="ts">
/**
 * PrimaryContactSelect
 *
 * Housing-module form element that renders a dropdown of the contacts linked to
 * the current Housing Application (workflow primary entity). The answer stores
 * the selected person's ENTITY guid; the Set_Primary_Contact special question
 * (pre-wired as the element default) stars that person as the application's
 * primary contact server-side when the answer saves.
 *
 * Data flow:
 *  1. Resolve the application entity guid from the injected form state's workflow.
 *  2. getLinkedPeople(entityGuid, "Entity") → contact list (404 ⇒ the workflow
 *     entity is not a housing application; the select is disabled).
 *  3. Join the application's SignalR contacts group; refetch the list whenever a
 *     LinkedPeopleUpdated event arrives (new contact linked).
 */
import type { HubConnection } from "@microsoft/signalr";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";
import {
  ApiError,
  FormElementType,
  type LinkedPersonDto,
  type PrimaryContactSelectFormElement,
  SpecialQuestion,
} from "~/api";

import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import {
  type FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props =
  defineProps<DefaultFormComponentProps<PrimaryContactSelectFormElement>>();

const formState = inject(formResponseInjectKey) as FormResponseState;
const answer = formState.useAnswer<FormElementType.PRIMARY_CONTACT_SELECT>(
  props.modelValue,
);

// ---------------------------------------------------------------------------
// Reactive state
// ---------------------------------------------------------------------------

const contacts = ref<LinkedPersonDto[]>([]);

/** True once getLinkedPeople has completed successfully at least once. */
const hasLoadedContacts = ref(false);

/** False after getLinkedPeople 404s — the workflow entity is not a housing application. */
const isHousingApplication = ref(true);

/** SignalR plumbing — the connection is shared with HMB viewer presence, never stop it here. */
let unsubscribe: (() => void) | null = null;
let connection: HubConnection | null = null;

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const elementProperties = computed(() => props.modelValue.properties);

/** Entity guid of the housing application (the workflow's primary entity). */
const applicationEntityGuid = computed(
  () => formState.workflow?.entityGuid?.toLowerCase() ?? null,
);

/** True when the saved answer references a contact no longer in the linked list. */
const selectedNotInList = computed(
  () =>
    !!answer.value &&
    hasLoadedContacts.value &&
    !contacts.value.some((c) => c.entityGuid === answer.value),
);

const isDisabled = computed(
  () =>
    props.isReadOnly ||
    !applicationEntityGuid.value ||
    !isHousingApplication.value,
);

// ---------------------------------------------------------------------------
// Methods
// ---------------------------------------------------------------------------

const loadContacts = async () => {
  if (!applicationEntityGuid.value) return;

  try {
    contacts.value = await apiClient.housingApplication.getLinkedPeople(
      applicationEntityGuid.value,
      "Entity",
    );
    hasLoadedContacts.value = true;

    // Default to the contact flagged as primary when no answer has been made yet.
    if (!answer.value) {
      const primaryContact = contacts.value.find((c) => c.isPrimaryContact);
      if (primaryContact) {
        onSelectionChange(primaryContact.entityGuid);
      }
    }
  } catch (error) {
    // 404 means the workflow entity is not a housing application — disable the
    // select. Anything else (network, auth) is transient: surface it and keep
    // the element enabled so a later broadcast/refetch can recover.
    if (error instanceof ApiError && error.status === 404) {
      isHousingApplication.value = false;
      contacts.value = [];
    } else {
      showError(error);
    }
  }
};

/**
 * Refetch the contact list whenever the dropdown is opened so the names shown
 * always reflect the latest edits to the linked people. Edits to a linked
 * person's own fields (e.g. renaming a co-applicant) do not broadcast
 * LinkedPeopleUpdated, so without this the list can trail behind recent changes.
 */
const onDropdownVisibleChange = (visible: boolean) => {
  if (visible && applicationEntityGuid.value && isHousingApplication.value) {
    loadContacts();
  }
};

/**
 * Update the answer and optimistically move local primary-contact state to the
 * selected contact. The server still confirms via Set_Primary_Contact and
 * LinkedPeopleUpdated.
 */
const onSelectionChange = (value: string | null) => {
  answer.value = value ?? undefined;

  if (
    value &&
    elementProperties.value.specialQuestion ===
      SpecialQuestion.SET_PRIMARY_CONTACT
  ) {
    contacts.value = contacts.value.map((contact) => ({
      ...contact,
      isPrimaryContact: contact.entityGuid === value,
    }));
  }
};

// ---------------------------------------------------------------------------
// Lifecycle — real-time updates via the shared housingApplication hub connection.
// The connection is shared with HMB viewer presence — never stop it here, only
// unsubscribe the handler and leave the contacts group.
// ---------------------------------------------------------------------------

onMounted(async () => {
  // Designer preview / form without a workflow: nothing to load or join
  if (!applicationEntityGuid.value) return;

  await loadContacts();
  if (!isHousingApplication.value) return;

  unsubscribe = await hubManager.subscribe(
    "housingApplication",
    "/housingApplication",
    "LinkedPeopleUpdated",
    (payload: { housingApplicationEntityGuid?: string }) => {
      if (
        payload?.housingApplicationEntityGuid?.toLowerCase() ===
        applicationEntityGuid.value
      ) {
        loadContacts();
      }
    },
  );

  connection = await hubManager.getHub(
    "housingApplication",
    "/housingApplication",
  );
  await connection?.invoke(
    "JoinApplicationContactsGroup",
    applicationEntityGuid.value,
  );
});

onUnmounted(async () => {
  unsubscribe?.();
  try {
    await connection?.invoke(
      "LeaveApplicationContactsGroup",
      applicationEntityGuid.value,
    );
  } catch {
    // The shared connection may already be closed
  }
});
</script>

<template>
  <div class="PrimaryContactSelectComponent">
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>

    <el-select
      :model-value="answer ?? undefined"
      :placeholder="elementProperties.placeholder ?? 'Select primary contact'"
      :disabled="isDisabled"
      popper-class="primaryContactSelect"
      no-data-text="No contacts linked to this application"
      @update:model-value="onSelectionChange($event ?? null)"
      @visible-change="onDropdownVisibleChange"
    >
      <el-option
        v-for="contact in contacts"
        :key="contact.entityGuid"
        :label="contact.name ?? 'Unnamed Contact'"
        :value="contact.entityGuid"
      >
        <span>{{ contact.name ?? "Unnamed Contact" }}</span>
        <el-tag
          v-if="contact.contactType"
          class="contact-type-tag"
          size="small"
          type="info"
        >
          {{ contact.contactType }}
        </el-tag>
      </el-option>
      <el-option
        v-if="selectedNotInList"
        :value="answer!"
        label="(contact no longer linked)"
        disabled
      />
    </el-select>
  </div>
</template>

<style lang="less">
.PrimaryContactSelectComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  .el-select__selection {
    text-align: right;
  }
  .el-select {
    .el-select__wrapper.is-disabled,
    .el-select__wrapper.is-disabled.is-hovering {
      box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      background-color: #f2f2f2;
      .el-select__placeholder {
        color: #444142;
      }
    }
  }
}

// in global popper
.primaryContactSelect {
  .el-select-dropdown__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2em;
  }
}

body.forPdf .PrimaryContactSelectComponent {
  .el-select__wrapper {
    box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
    background-color: transparent !important;
    .el-select__placeholder {
      color: black !important;
    }
  }
}
</style>
